import { useEffect, useState } from 'react';
import { GridInitialState } from '@mui/x-data-grid-pro';
import { getUserSubject } from '../../../../../store/User';
import { ApMap, fetchApMaps, getTallyApMapsLoadingSubject, getTallyApMapsSubject } from '../../../../../store/ApMap';
import { DefaultApMapGridColumnDefs } from '../../ColumnDefs/ApMapGridColumnDefs';
import { UpstackDataGrid } from '../../../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { getGridColumns, getInitialState } from '../../../../Compounds/UpstackDataGrid/helpers';
import { PageCard } from '../../../../Compounds/CardWithTitle';
import { getDefaultPreferences, GridPreferences } from '../../../../../store/UserCustomizations';
import { Icon } from '../../../../Atoms/Icon';
import { TextSpan } from '../../../../Atoms/Text';
import { GridCustomColDef } from '../../../../types';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';
import { NewMapDialog } from '../../../../Compounds/Mapping/NewMapDialog/NewMapDialog';
import { EntityType } from '../../../../../store/Search';
import { PageLayout } from '../../../../PageLayout';

export const ApMapTab = () => {
  const [apMapsLoading, setApMapsLoading] = useState<boolean>(false);
  const [apMaps, setApMaps] = useState<ApMap[]>([]);
  const [apMapColumns, setApMapColumns] = useState<GridCustomColDef[]>(DefaultApMapGridColumnDefs);
  const [isMapFormOpen, setIsMapFormOpen] = useState<boolean>(false);
  const [gridSettings, setGridSettings] = useState<GridPreferences>(
    getDefaultPreferences()?.content['apMapGrid'] as GridPreferences
  );
  const [initialState, setInitialState] = useState<GridInitialState>();

  useEffect(() => {
    const loadingSubscription = getTallyApMapsLoadingSubject().subscribe((loading) => setApMapsLoading(loading));
    const apMapsSub = getTallyApMapsSubject().subscribe((maps) => setApMaps(maps));
    const userSub = getUserSubject().subscribe((usr) => {
      if (usr?.preferences?.content?.apMapGrid) {
        setGridSettings(usr.preferences.content.apMapGrid);
        const state = getInitialState(usr.preferences.content.apMapGrid, DefaultApMapGridColumnDefs, 'apMapGrid');
        setInitialState(state);
      }
    });

    fetchApMaps();

    return () => {
      if (loadingSubscription) loadingSubscription.unsubscribe();
      if (apMapsSub) apMapsSub.unsubscribe();
      if (userSub) userSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!apMapsLoading && apMaps.length > 0) {
      setApMapColumns(getGridColumns(apMaps, DefaultApMapGridColumnDefs, 'apMapGrid'));
    }
  }, [apMapsLoading, apMaps.length]);

  const handleNewMap = () => {
    setIsMapFormOpen(true);
  };

  const cardActions = () => (
    <button
      className="flex items-center space-x-2"
      onClick={handleNewMap}
      data-cy="new-ap-map-button"
      data-testid="new-ap-map-button">
      <Icon type="plus" />
      <TextSpan
        color="indigo"
        size="sm14">
        New AP Map
      </TextSpan>
    </button>
  );

  return (
    <PageLayout
      className="w-full"
      dataTestId="ap-map-page"
      dataCy="ap-map-page-card"
      pageTitle="">
      <PageCard
        dataTestId="ap-map-management"
        dataCy="ap-map-management"
        childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
        title="AP Map"
        cardActions={cardActions()}>
        <>
          {!initialState ||
            (apMapsLoading && (
              <div className="flex h-[45rem] m-auto">
                <ComponentSpinner label="Loading data..." />
              </div>
            ))}
          {initialState && !apMapsLoading && (
            <UpstackDataGrid
              title="AP Map"
              rows={apMaps}
              columns={apMapColumns}
              page="apMapGrid"
              gridSettings={gridSettings}
              showSearch={true}
              initialState={initialState}
              apiFilter={false}
              sxStyles={{ borderWidth: '0', borderBottomLeftRadius: '.75rem', borderBottomRightRadius: '.75rem' }}
            />
          )}
          <NewMapDialog
            open={isMapFormOpen}
            setOpen={setIsMapFormOpen}
            mapType={EntityType.AP}
          />
        </>
      </PageCard>
    </PageLayout>
  );
};
